import React, { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { dispatchUpdateOrders } from "../../utils/orders";
import { setSelectOrderDriverModalOrder } from "../../store/actions/driversSlice";
import {
  acceptOrderThunk,
  cancelDriverRequestThunk,
  fetchOrderPrintDetails,
  finishOrderThunk,
  packedOrderThunk,
  readyToPickupOrderThunk,
  selectAllOrders,
  setShowOrderInfosModal,
} from "../../store/actions/ordersSlice";
import OrdersRow from "../orders/table/ordersRow";
import LoadingBox from "../indicators/LoadingBox";
import DashboardNoOrdersMessage from "./DashboardNoOrdersMessage";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { Button, Fade } from "@mui/material";
import ExpandCircleDownIcon from "@mui/icons-material/ExpandCircleDown";
import { setCenter } from "../../store/actions/mapSlice";
import { dispatchSnackBarMessage } from "../../utils/snackbar";
import { ERROR_QZ_TRAY_REQUIRED } from "../../common/helpersTxt";
import { extraDelivery } from "../../api/order";
import ExtraDeliveryDialog from "../dialogs/orders/ExtraDeliveryDialog";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CompactOrderCard from "./OrderCardComponent/CompactOrderCard";

const DashboardStatusOrderList = ({
  status = "orders-on-way",
  viewMode = "list",
}) => {
  const dispatch = useDispatch();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const [selectedDriver, setSelectedDriver] = useState(false);
  const [reason, setReason] = useState("");

  const [driverError, setDriverError] = useState(null);
  const [error, setError] = useState(null);

  const orders = useSelector(selectAllOrders);
  const loadingOrders = useSelector((state) => state.orders.loadingOrders);
  const showButtonMore = useSelector((state) => state.orders.showButtonMore);
  const showOrderInfosModal = useSelector(
    (state) => state.orders.showOrderInfosModal
  );
  const showSelectOrderDriverModal = useSelector(
    (state) => state.drivers.showSelectOrderDriverModal
  );

  const handleOrderAction = useCallback(
    (action, order) => {
      dispatch(action({ order }));
    },
    [dispatch]
  );

  const handleShowMore = useCallback(() => {
    dispatchUpdateOrders(false);
  }, []);

  const handleSelectDriver = useCallback(
    (order) => {
      dispatch(
        setSelectOrderDriverModalOrder({
          show: !showSelectOrderDriverModal,
          order: order,
        })
      );
    },
    [dispatch, showSelectOrderDriverModal]
  );

  const handleCancelDriverRequest = useCallback(
    (order) => {
      dispatch(
        cancelDriverRequestThunk({
          order_id: order.order_id,
        })
      );
    },
    [dispatch]
  );

  const handleOrderModal = useCallback(
    (order) => {
      dispatch(
        setShowOrderInfosModal({
          orderId: order.order_id,
          show: !showOrderInfosModal,
        })
      );
    },
    [dispatch, showOrderInfosModal]
  );

  const handleChangeMapCenter = useCallback(
    (lat, lng) => {
      const latNumber = parseFloat(lat);
      const lngNumber = parseFloat(lng);

      dispatch(
        setCenter({
          lat: latNumber,
          lng: lngNumber,
        })
      );
    },
    [dispatch]
  );

  const handleExtraDelivery = useCallback((order) => {
    setSelectedOrder(order);
    setDialogOpen(true);
  }, []);

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedOrder(null);
  };

  const handleDialogConfirm = async () => {
    try {
      if (!selectedDriver || selectedDriver === 0) {
        setDriverError("Selecione um motorista válido antes de continuar.");
        return;
      } else {
        setDriverError(null);
      }

      await extraDelivery({
        orderId: selectedOrder.order_id,
        driver_id: selectedDriver,
      });
      handleDialogClose();
    } catch (e) {
      console.error("Error processing extra delivery:", e);
    }
  };

  const printOrder = useCallback(
    async (orderId, e) => {
      try {
        e.stopPropagation();
        await dispatch(fetchOrderPrintDetails({ orderId })).unwrap();
      } catch (error) {
        console.error("Failed to print order:", error);
        dispatchSnackBarMessage(
          `dashboard-status-orders-list-print-${new Date()}`,
          ERROR_QZ_TRAY_REQUIRED,
          "error"
        );
      }
    },
    [dispatch]
  );

  const validateSelection = (value) => {
    if (!value) {
      setError("Selecione um motorista antes de continuar.");
    } else {
      setError(null);
    }
  };

  const handleSelectChange = useCallback(
    (e, value) => {
      if (value?.isBlocked) {
        e.preventDefault();
        return;
      }

      validateSelection(value);
      setSelectedDriver(value.value);
    },
    [dispatch]
  );

  // Card view handlers
  const handleViewDetails = useCallback(
    (order) => {
      handleOrderModal(order);
    },
    [handleOrderModal]
  );

  const handleAcceptOrder = useCallback(
    (order) => {
      handleOrderAction(acceptOrderThunk, order);
    },
    [handleOrderAction]
  );

  const handlePrintOrder = useCallback(
    async (orderId, e) => {
      await printOrder(orderId, e);
    },
    [printOrder]
  );

  // Get empty state content based on status
  const getEmptyStateContent = () => {
    switch (status) {
      case "orders-on-way":
        return {
          title: "Nenhum pedido",
          body: "Não há pedidos no momento.",
          icon: <LocalShippingIcon />,
        };
      case "orders-completed":
        return {
          title: "Nenhum pedido entregue",
          body: "Não há pedidos entregues no período selecionado.",
          icon: <CheckCircleIcon />,
        };
      default:
        return {
          title: "Nenhum pedido encontrado",
          body: "Não há pedidos neste status no momento.",
          icon: <LocalShippingIcon />,
        };
    }
  };

  const OrderRows = useMemo(() => {
    return orders.map((item, index) => (
      <OrdersRow
        key={item.order_id}
        item={item}
        dashboard
        showCenter={false}
        onlyModalAction={false}
        acceptOrder={() => handleOrderAction(acceptOrderThunk, item)}
        readyToPickupOrder={() =>
          handleOrderAction(readyToPickupOrderThunk, item)
        }
        finishOrder={() => handleOrderAction(finishOrderThunk, item)}
        handleOrderModal={() => handleOrderModal(item)}
        handleSelectDriver={() => handleSelectDriver(item)}
        changeMapCenter={handleChangeMapCenter}
        cancelDriverRequest={() => handleCancelDriverRequest(item)}
        packedOrder={() => handleOrderAction(packedOrderThunk, item)}
        extraDelivery={() => handleExtraDelivery(item)}
        printOrder={printOrder}
      />
    ));
  }, [
    orders,
    printOrder,
    handleOrderAction,
    handleOrderModal,
    handleSelectDriver,
    handleChangeMapCenter,
    handleCancelDriverRequest,
  ]);

  const OrderCards = useMemo(() => {
    return orders.map((order) => (
      <Grid
        item
        xs={12}
        sm={12} // Full width on mobile
        md={12} // Full width on tablet if list view constrained
        lg={12}
        xl={6} // Two columns on very wide screens
        key={order.order_id || order.order_index}
      >
        <CompactOrderCard
          order={order}
          onViewDetails={handleViewDetails}
          onAcceptOrder={handleAcceptOrder}
          onPrintOrder={handlePrintOrder}
        />
      </Grid>
    ));
  }, [orders, handleViewDetails, handleAcceptOrder, handlePrintOrder]);

  const renderContent = () => {
    const emptyState = getEmptyStateContent();

    if (orders.length === 0) {
      return (
        <DashboardNoOrdersMessage
          title={emptyState.title}
          body={emptyState.body}
          icon={emptyState.icon}
        />
      );
    }

    if (viewMode === "grid") {
      return (
        <Grid container spacing={2}>
          {OrderCards}
        </Grid>
      );
    }

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
        {OrderRows}
      </Box>
    );
  };

  const renderLoadMoreButton = () => {
    if (orders.length === 0) return null;

    return (
      <Box
        alignItems="center"
        justifyContent="center"
        sx={{
          display: "flex",
          mt: 2,
        }}
      >
        <Button
          title="Carregar mais"
          size="large"
          variant="outlined"
          color="inherit"
          disabled={!showButtonMore || loadingOrders}
          startIcon={<ExpandCircleDownIcon fontSize="inherit" />}
          onClick={handleShowMore}
        >
          {loadingOrders ? "Carregando..." : "Mostrar mais"}
        </Button>
      </Box>
    );
  };

  if (loadingOrders) {
    return <LoadingBox height="100%" />;
  }

  return (
    <>
      <Fade in={!loadingOrders} mountOnEnter unmountOnExit>
        <Box className="order-list-step">
          {renderContent()}
          {renderLoadMoreButton()}
        </Box>
      </Fade>
      <ExtraDeliveryDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        driverError={driverError}
        handleSelectChange={handleSelectChange}
        onConfirm={handleDialogConfirm}
        reason={reason}
        setReason={setReason}
      />
    </>
  );
};

export default React.memo(DashboardStatusOrderList);
